/**
 * Articles context for React state management.
 * 
 * This provides article state and methods using React Context and useReducer
 * for shared state management across the application, eliminating duplicate API calls.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  createArticle,
  createArticlesBulk,
  getArticles,
  getArticle,
  updateArticle,
  deleteArticle,
  regenerateSummary,
  refetchContent,
  getArticleStats,
  markArticleRead,
  markArticleUnread,
  getBatchArticleStatus,
} from '../services/articles';
import { initialState, articlesReducer } from './articlesReducer';

// Articles context
const ArticlesContext = createContext();

/**
 * Articles Context Provider Component
 */
export const ArticlesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(articlesReducer, initialState);

  /**
   * Fetch articles with pagination and filters
   */
  const inFlightArticlesRef = React.useRef(Promise.resolve());
  const fetchArticles = useCallback(async (params = {}) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const queryParams = {
        page: params.page || state.pagination.page,
        per_page: params.per_page || state.pagination.per_page,
        ...state.filters,
        ...params,
      };
      
      // chain requests to avoid overlapping list fetches
      inFlightArticlesRef.current = inFlightArticlesRef.current.catch(() => {}).then(() => 
        getArticles(queryParams, { headers: { 'X-Request-Group': 'articles-list' } })
      );
      const response = await inFlightArticlesRef.current;
      
      dispatch({
        type: 'FETCH_ARTICLES_SUCCESS',
        payload: {
          articles: response.articles,
          pagination: {
            page: response.page,
            per_page: response.per_page,
            total: response.total,
            pages: response.total_pages, // Backend returns total_pages, not pages
          },
          append: params.append || false,
        },
      });
      
      return response;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [state.filters, state.pagination.page, state.pagination.per_page]);

  /**
   * Fetch a specific article by ID
   */
  const fetchArticle = useCallback(async (articleId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const article = await getArticle(articleId);
      dispatch({ type: 'FETCH_ARTICLE_SUCCESS', payload: article });
      return article;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Create a new article from URL (server-side processing)
   */
  const addArticle = useCallback(async (articleData) => {
    dispatch({ type: 'SET_CREATING', payload: true });

    try {
      const newArticle = await createArticle(articleData);
      dispatch({ type: 'CREATE_ARTICLE_SUCCESS', payload: newArticle });
      return newArticle;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Create multiple articles from URLs in bulk
   */
  const addArticlesBulk = useCallback(async (urls) => {
    dispatch({ type: 'SET_CREATING', payload: true });

    try {
      const response = await createArticlesBulk({ urls });

      // Add successful articles to state
      const successfulArticles = response.results
        .filter(result => result.success && result.article)
        .map(result => result.article);

      // Dispatch all successful articles
      successfulArticles.forEach(article => {
        dispatch({ type: 'CREATE_ARTICLE_SUCCESS', payload: article });
      });

      dispatch({ type: 'SET_CREATING', payload: false });
      return response;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Update an existing article
   */
  const updateExistingArticle = useCallback(async (articleId, updateData) => {
    dispatch({ type: 'SET_UPDATING', payload: true });
    
    try {
      const updatedArticle = await updateArticle(articleId, updateData);
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: updatedArticle });
      return updatedArticle;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Delete an article
   */
  const removeArticle = useCallback(async (articleId) => {
    dispatch({ type: 'SET_DELETING', payload: true });
    
    try {
      await deleteArticle(articleId);
      dispatch({ type: 'DELETE_ARTICLE_SUCCESS', payload: articleId });
      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Regenerate article summary
   */
  const regenerateArticleSummary = useCallback(async (articleId) => {
    dispatch({ type: 'SET_REGENERATING', payload: true });
    
    try {
      const updatedArticle = await regenerateSummary(articleId);
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: updatedArticle });
      return updatedArticle;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Re-fetch article content from original URL and regenerate summary
   */
  const refetchArticleContent = useCallback(async (articleId) => {
    dispatch({ type: 'SET_REFETCHING', payload: true });
    
    try {
      const updatedArticle = await refetchContent(articleId);
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: updatedArticle });
      return updatedArticle;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Fetch article statistics with caching and deduplication
   */
  const fetchStats = useCallback(async (force = false) => {
    // Skip if already loading stats to prevent duplicate calls
    if (state.isLoadingStats && !force) {
      return state.stats;
    }

    try {
      dispatch({ type: 'SET_LOADING_STATS', payload: true });
      const stats = await getArticleStats(force);
      dispatch({ type: 'FETCH_STATS_SUCCESS', payload: stats });
      return stats;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [state.isLoadingStats, state.stats]);

  /**
   * Set filters for article list
   */
  const setFilters = useCallback((newFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Clear current article
   */
  const clearCurrentArticle = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_ARTICLE' });
  }, []);

  /**
   * Clear operation states (isRefetching, isRegenerating, isUpdating)
   * Used when switching between articles to prevent stale operation states
   */
  const clearOperationStates = useCallback(() => {
    dispatch({ type: 'CLEAR_OPERATION_STATES' });
  }, []);

  /**
   * Load more articles (pagination)
   */
  const loadNextPage = useCallback(async () => {
    if (state.pagination.page < state.pagination.pages) {
      await fetchArticles({ page: state.pagination.page + 1, append: true });
    }
  }, [state.pagination.page, state.pagination.pages, fetchArticles]);

  /**
   * Refresh current page
   */
  const refreshArticles = useCallback(async () => {
    await fetchArticles({ page: 1 });
  }, [fetchArticles]);

  /**
   * Go to specific page
   */
  const goToPage = useCallback(async (page) => {
    if (page >= 1 && page <= state.pagination.pages && page !== state.pagination.page) {
      await fetchArticles({ page });
    }
  }, [state.pagination.page, state.pagination.pages, fetchArticles]);

  /**
   * Change items per page
   */
  const setPerPage = useCallback(async (perPage) => {
    dispatch({ type: 'SET_PER_PAGE', payload: perPage });
    await fetchArticles({ page: 1, per_page: perPage });
  }, [fetchArticles]);

  /**
   * Mark an article as read
   */
  const markAsRead = useCallback(async (articleId) => {
    dispatch({ type: 'SET_UPDATING', payload: true });
    
    try {
      const updatedArticle = await markArticleRead(articleId);
      
      // This will trigger the sorting in the reducer
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: updatedArticle });
      
      return updatedArticle;
    } catch (error) {
      console.error('Error in markAsRead:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Mark an article as unread
   */
  const markAsUnread = useCallback(async (articleId) => {
    dispatch({ type: 'SET_UPDATING', payload: true });
    
    try {
      const updatedArticle = await markArticleUnread(articleId);
      
      // This will trigger the sorting in the reducer
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: updatedArticle });
      
      return updatedArticle;
    } catch (error) {
      console.error('Error in markAsUnread:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Check processing status for multiple articles in batch
   * This dramatically reduces API calls compared to individual status checks
   */
  const inFlightStatusRef = React.useRef(Promise.resolve());
  const checkBatchStatus = useCallback(async (articleIds) => {
    if (!articleIds || articleIds.length === 0) return {};

    try {
      // chain requests to avoid overlapping status polls
      inFlightStatusRef.current = inFlightStatusRef.current.catch(() => {}).then(() =>
        getBatchArticleStatus(articleIds, { headers: { 'X-Request-Group': 'batch-status' } })
      );
      const response = await inFlightStatusRef.current;
      
      // Convert array response to map for easy lookup
      const statusMap = {};
      response.statuses.forEach(status => {
        statusMap[status.article_id] = status;
      });
      
      return statusMap;
    } catch (error) {
      console.error('Error in checkBatchStatus:', error);
      throw error;
    }
  }, []);

  /**
   * Update articles with batch status results
   * This updates the local state without triggering a full refetch
   * For completed/failed articles, includes display fields (title, summary, etc.)
   */
  const updateArticlesWithBatchStatus = useCallback((statusMap) => {
    if (!statusMap || Object.keys(statusMap).length === 0) return;

    // Update articles in state with new status information + display fields
    const updatedArticles = state.articles.map(article => {
      const statusInfo = statusMap[article.id];
      if (statusInfo && statusInfo.status !== article.processing_status) {
        // Merge all available fields from statusInfo
        // For completed/failed articles, backend includes title, summary, domain, author, language
        return {
          ...article,
          processing_status: statusInfo.status,
          processing_error: statusInfo.error,
          processing_attempts: statusInfo.attempts,
          processed_at: statusInfo.processed_at,
          // Include display fields if present (only for completed/failed articles)
          ...(statusInfo.title && { title: statusInfo.title }),
          ...(statusInfo.summary && { summary: statusInfo.summary }),
          ...(statusInfo.domain && { domain: statusInfo.domain }),
          ...(statusInfo.author && { author: statusInfo.author }),
          ...(statusInfo.language && { language: statusInfo.language })
        };
      }
      return article;
    });

    // Only dispatch if there were actual changes
    const hasChanges = updatedArticles.some((article, index) =>
      article.processing_status !== state.articles[index].processing_status
    );

    if (hasChanges) {
      dispatch({
        type: 'FETCH_ARTICLES_SUCCESS',
        payload: {
          articles: updatedArticles,
          pagination: state.pagination,
          append: false,
        },
      });
    }
  }, [state.articles, state.pagination]);

  // Auto-fetch stats on mount
  useEffect(() => {
    fetchStats().catch(console.error);
  }, []); // Empty dependency to run once

  const value = {
    // State
    articles: state.articles,
    currentArticle: state.currentArticle,
    stats: state.stats,
    pagination: state.pagination,
    filters: state.filters,
    isLoading: state.isLoading,
    isCreating: state.isCreating,
    isUpdating: state.isUpdating,
    isDeleting: state.isDeleting,
    isRegenerating: state.isRegenerating,
    isRefetching: state.isRefetching,
    isLoadingStats: state.isLoadingStats,
    error: state.error,
    
    // Actions
    fetchArticles,
    fetchArticle,
    addArticle,
    addArticlesBulk,
    updateExistingArticle,
    removeArticle,
    regenerateArticleSummary,
    refetchArticleContent,
    fetchStats,
    setFilters,
    clearError,
    clearCurrentArticle,
    clearOperationStates,
    loadNextPage,
    refreshArticles,
    goToPage,
    setPerPage,
    markAsRead,
    markAsUnread,
    checkBatchStatus,
    updateArticlesWithBatchStatus,
    
    // Computed values
    hasNextPage: state.pagination.page < state.pagination.pages,
    isEmpty: state.articles.length === 0 && !state.isLoading,
  };

  return (
    <ArticlesContext.Provider value={value}>
      {children}
    </ArticlesContext.Provider>
  );
};

/**
 * useArticles hook for consuming articles context
 */
export const useArticles = () => {
  const context = useContext(ArticlesContext);
  
  if (!context) {
    throw new Error('useArticles must be used within an ArticlesProvider');
  }
  
  return context;
};